import models.LimitedTimeGameModes

/** How a cosmetic (base skin or effect) is unlocked. */
enum class DiceSkinTier { FREE, SILVER, GOLD, LUCKY, CREATOR, PRACTICE, LIMITED_PLUS }

private fun DiceSkinTier.isUnlocked(
    adsRemoved: Boolean,
    bankoPlusActive: Boolean,
    luckyUnlocked: Boolean,
    creatorUnlocked: Boolean,
    practiceUnlocked: Boolean,
    limitedTimeActive: Boolean,
    limitedTimeEarned: Boolean,
): Boolean {
    return when (this) {
        DiceSkinTier.FREE -> true
        DiceSkinTier.SILVER -> adsRemoved || bankoPlusActive
        DiceSkinTier.GOLD -> bankoPlusActive
        DiceSkinTier.LUCKY -> luckyUnlocked
        DiceSkinTier.CREATOR -> creatorUnlocked
        DiceSkinTier.PRACTICE -> practiceUnlocked
        DiceSkinTier.LIMITED_PLUS -> bankoPlusActive && (limitedTimeActive || limitedTimeEarned)
    }
}

// ---- Layer 1: base skin (the die material) ---------------------------------------------------

/**
 * The die's base material — color/finish of the face + pips, plus an optional on-surface fire
 * (the "Ember" looks, which fill the face and so are a material, not a layerable overlay). Stored by
 * [id] on the synced Player doc / PlayerPreferences. Combined with a separate [DiceHat] at render.
 */
enum class DiceSkin(
    val id: String,
    val displayName: String,
    val tier: DiceSkinTier,
    val limitedTimeSettingId: String? = null,
) {
    DEFAULT("default", "Classic", DiceSkinTier.FREE),
    SILVER("silver", "Silver", DiceSkinTier.SILVER),
    GOLD("gold", "Gold", DiceSkinTier.GOLD),
    EMBER("ember", "Ember", DiceSkinTier.GOLD),
    BLUE_EMBER("blue_ember", "Blue Ember", DiceSkinTier.GOLD),
    USA("usa", "USA", DiceSkinTier.LIMITED_PLUS, LimitedTimeGameModes.FIREWORKS_SETTING_ID),
    HARVEST("harvest", "Autumn", DiceSkinTier.LIMITED_PLUS, LimitedTimeGameModes.FALL_SETTING_ID),
    HALLOWEEN("halloween", "Haunted", DiceSkinTier.LIMITED_PLUS, LimitedTimeGameModes.HALLOWEEN_SETTING_ID),
    TRAINING("training", "Training Wheels", DiceSkinTier.PRACTICE),
    LUCKY("lucky", "Lucky", DiceSkinTier.LUCKY),
    CREATOR("creator", "Creator", DiceSkinTier.CREATOR);

    fun isUnlocked(
        adsRemoved: Boolean,
        bankoPlusActive: Boolean,
        luckyUnlocked: Boolean,
        creatorUnlocked: Boolean,
        practiceUnlocked: Boolean,
        activeLimitedTimeSettingIds: Set<String> = LimitedTimeGameModes.activeSettingIdsNow(),
        earnedLimitedTimeSkinIds: Set<String> = LimitedTimeDiceEntitlements.earnedSkinIds.value,
    ): Boolean = tier.isUnlocked(
        adsRemoved = adsRemoved,
        bankoPlusActive = bankoPlusActive,
        luckyUnlocked = luckyUnlocked,
        creatorUnlocked = creatorUnlocked,
        practiceUnlocked = practiceUnlocked,
        limitedTimeActive = limitedTimeSettingId in activeLimitedTimeSettingIds,
        limitedTimeEarned = id in earnedLimitedTimeSkinIds,
    )

    fun isVisible(
        activeLimitedTimeSettingIds: Set<String> = LimitedTimeGameModes.activeSettingIdsNow(),
        earnedLimitedTimeSkinIds: Set<String> = LimitedTimeDiceEntitlements.earnedSkinIds.value,
    ): Boolean = limitedTimeSettingId == null ||
        limitedTimeSettingId in activeLimitedTimeSettingIds ||
        id in earnedLimitedTimeSkinIds

    companion object {
        fun fromId(id: String?): DiceSkin = entries.firstOrNull { it.id == id } ?: DEFAULT
    }
}

fun visibleDiceSkinsForPicker(
    adsRemoved: Boolean,
    bankoPlusActive: Boolean,
    luckyUnlocked: Boolean,
    creatorUnlocked: Boolean,
    practiceUnlocked: Boolean,
    activeLimitedTimeSettingIds: Set<String> = LimitedTimeGameModes.activeSettingIdsNow(),
    earnedLimitedTimeSkinIds: Set<String> = LimitedTimeDiceEntitlements.earnedSkinIds.value,
): List<DiceSkin> {
    return DiceSkin.entries
        .filter { skin ->
            skin.isVisible(
                activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
            )
        }
        .sortedWith(
            compareBy<DiceSkin> { skin ->
                skin.pickerCategory(
                    adsRemoved = adsRemoved,
                    bankoPlusActive = bankoPlusActive,
                    luckyUnlocked = luckyUnlocked,
                    creatorUnlocked = creatorUnlocked,
                    practiceUnlocked = practiceUnlocked,
                    activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                    earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
                )
            }.thenBy { it.ordinal }
        )
}

private fun DiceSkin.pickerCategory(
    adsRemoved: Boolean,
    bankoPlusActive: Boolean,
    luckyUnlocked: Boolean,
    creatorUnlocked: Boolean,
    practiceUnlocked: Boolean,
    activeLimitedTimeSettingIds: Set<String>,
    earnedLimitedTimeSkinIds: Set<String>,
): Int {
    return when {
        this == DiceSkin.DEFAULT -> 0
        limitedTimeSettingId in activeLimitedTimeSettingIds -> 1
        id in earnedLimitedTimeSkinIds -> 2
        isUnlocked(
            adsRemoved = adsRemoved,
            bankoPlusActive = bankoPlusActive,
            luckyUnlocked = luckyUnlocked,
            creatorUnlocked = creatorUnlocked,
            practiceUnlocked = practiceUnlocked,
            activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
            earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
        ) -> 3
        tier == DiceSkinTier.SILVER || tier == DiceSkinTier.GOLD -> 4
        tier == DiceSkinTier.LUCKY || tier == DiceSkinTier.CREATOR || tier == DiceSkinTier.PRACTICE -> 5
        else -> 6
    }
}

// ---- Layer 2: hat (a cosmetic worn on top of the die) ----------------------------------------

/** Color ramp for a flame. */
enum class FirePalette { BLUE, ORANGE }

/**
 * A cosmetic worn on top of the die, composing with any base [DiceSkin]. Currently flame hats (an
 * aura of flames rising off the top that bleeds past the die); the slot is built to take other hat
 * kinds later (crown, halo, party hat…). Stored by [id] on the synced Player doc / PlayerPreferences.
 */
enum class DiceHat(
    val id: String,
    val displayName: String,
    val tier: DiceSkinTier,
    // Non-null → a procedural flame hat in this palette. (Future non-flame hats would add their own
    // render kind here.)
    val palette: FirePalette?,
) {
    NONE("none", "None", DiceSkinTier.FREE, null),
    BLAZE("blaze", "Blaze", DiceSkinTier.GOLD, FirePalette.ORANGE),
    BLUE_BLAZE("blue_blaze", "Blue Blaze", DiceSkinTier.GOLD, FirePalette.BLUE);

    fun isUnlocked(
        adsRemoved: Boolean,
        bankoPlusActive: Boolean,
        luckyUnlocked: Boolean,
        creatorUnlocked: Boolean,
    ): Boolean = tier.isUnlocked(
        adsRemoved = adsRemoved,
        bankoPlusActive = bankoPlusActive,
        luckyUnlocked = luckyUnlocked,
        creatorUnlocked = creatorUnlocked,
        // No hat is practice-gated (the training reward is a base skin only).
        practiceUnlocked = false,
        limitedTimeActive = false,
        limitedTimeEarned = false,
    )

    companion object {
        fun fromId(id: String?): DiceHat = entries.firstOrNull { it.id == id } ?: NONE
    }
}
